import _ from 'lodash'
import React, { CSSProperties } from 'react'
import { objectToString } from 'shared-libs/helpers/formulas/modules'

import { Entity } from 'shared-libs/models/entity'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { StatusBadge } from 'browser/components/atomic-elements/atoms/status-badge/status-badge'
import { EntityFormSheet, EntityFormSheetMode } from '../entity-form-sheet'
import { LoadingSpinner } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import { SheetContext } from 'browser/components/atomic-elements/atoms/sheet/sheet-manager'
import 'browser/components/atomic-elements/organisms/entity/entity-preview-card/_entity-preview-card.scss'
import apis from 'browser/app/models/apis'
import classNames from 'classnames'
import { evaluateExpressionWithScopes } from 'shared-libs/helpers/evaluation'
import { AsyncFormulaResolver } from 'shared-libs/helpers/async-formula-resolver'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import { Settings } from 'browser/app/models/settings'
import { Label, ILabelProps } from 'browser/components/atomic-elements/atoms/label/label'

enum EntityPreviewCardCellType {
  INFO = 'info',
  IDENTIFIER = 'identifier',
  INFO_FROM_ARRAY = 'info_from_array',
  STATUS = 'status',
  NOTES = 'notes',
}

interface IEntityPreviewCardCellProps {
  path?: string
  formula?: string
  value?: any
  label?: string
  color?: string
  labelPath?: string
  valuePath?: string
  type?: EntityPreviewCardCellType
}

export interface IEntityPreviewCardProps extends IBaseProps {
  entity?: any
  frames?: FramesManager
  value?: any // Needed when used in a list context so that the renderer can properly determine the value
  title?: string
  data?: IEntityPreviewCardCellProps[]
  label?: string
  labelProps?: ILabelProps
  onClick?: (args) => void
  isClickable?: boolean // if custom onClick is provided, setting this to false will only disable hover state
  entityEditSchema?: string
  entityDetailSchema?: string
  entitySheetMode?: EntityFormSheetMode
  openOverlay?: any
  isListOption?: boolean
  // dodges `isHorizontalLayout` provided by a higher context (entity-page). We may want to refactor
  // this component to not manage its own label and instead wrap in LabeledFieldFactory as needed?
  // Bug discussion: https://withvector.slack.com/archives/CQQTG221J/p1776893468455469?thread_ts=1776872284.718789&cid=CQQTG221J
  useHorizontalLayout?: boolean
  // allows displaying the entity type above the title
  showEntityType?: boolean
  style?: CSSProperties
}

interface IEntityPreviewCardState {
  entity: Entity
  isLoading: boolean
}

/**
 * EntityPreviewCard - A card component to display a formatted summary of an entity's information.
 * Attempts to open an entity edit sheet on click, unless a custom onClick handler is provided.
 * If no edit sheet schema is found, opens the entity detail page in a new tab.
 */
export class EntityPreviewCard extends React.PureComponent<
  IEntityPreviewCardProps,
  IEntityPreviewCardState
> {
  static contextType = SheetContext
  declare context: React.ContextType<typeof SheetContext>
  private store: any
  private settings: Settings
  // Resolves cell formulas that return a Promise (e.g. GEOPOSITION()), caching
  // results and re-rendering as they settle.
  private formulaResolver = new AsyncFormulaResolver(() => this.forceUpdate())

  public static defaultProps: Partial<IEntityPreviewCardProps> = {
    isClickable: true,
    entityDetailSchema: 'uiSchema.web.entityDetailSheet',
    entityEditSchema: 'uiSchema.web.entityEditSheet',
    entitySheetMode: EntityFormSheetMode.Edit,
    showEntityType: false,
  }

  constructor(props) {
    super(props)
    this.store = apis.getStore()
    this.state = {
      entity: null,
      isLoading: false,
    }
    this.settings = apis.getSettings()
  }

  public componentWillUnmount() {
    this.formulaResolver.dispose()
  }

  public componentDidMount() {
    const { entity, value } = this.props
    if (entity?.entityId || value?.entityId) {
      this.fetchValueEntity(value?.entityId ?? entity?.entityId)
    } else if (entity) {
      this.setState({ entity, isLoading: false })
    }
  }

  public componentDidUpdate(prevProps) {
    const { entity, value } = this.props
    const entityId: string = value?.entityId ?? entity?.entityId ?? entity?.uniqueId
    const prevEntityId: string =
      prevProps?.value?.entityId ?? prevProps?.entity?.entityId ?? prevProps?.entity?.uniqueId

    if (entityId !== prevEntityId) {
      if (_.isNil(entityId)) {
        this.setState({
          entity: undefined,
          isLoading: false,
        })
      } else if (entity?.entityId || value?.entityId) {
        this.fetchValueEntity(value?.entityId ?? entity?.entityId)
      } else {
        this.setState({ entity, isLoading: false })
      }
    }
  }

  public render() {
    if (this.state.isLoading) {
      return (
        <div className="pa3 pt4 bt b--light-gray">
          <LoadingSpinner size="xs" />
        </div>
      )
    }

    return this.renderContent()
  }

  private renderContent() {
    const {
      isClickable,
      className,
      onClick,
      isListOption,
      useHorizontalLayout,
      label,
      labelProps,
      style,
    } = this.props

    const enableOnClick = isClickable || !_.isNil(onClick)

    return (
      <div
        // Allow section components to detect when there is no value
        className={classNames('c-formGroup', {
          'c-formGroup--hasNoValue': _.isNil(this.state.entity),
          'c-formGroup--isHorizontal': useHorizontalLayout,
        })}
      >
        {label && !isListOption && (
          <Label
            text={label}
            {...labelProps}
            className={classNames(labelProps?.className, {
              'c-label--isHorizontal': useHorizontalLayout,
            })}
          />
        )}
        {this.state.entity && (
          <div
            className={classNames(
              'c-entity-preview-card',
              { 'c-entity-preview-card--isClickable': enableOnClick },
              { 'c-entity-preview-card--isListOption': isListOption },
              className
            )}
            style={style}
            role={enableOnClick ? 'button' : undefined}
            tabIndex={0}
            onTouchStart={enableOnClick ? this.handleOnClick : undefined}
            onClick={enableOnClick ? this.handleOnClick : undefined}
            onKeyDown={(e) => {
              if (!enableOnClick) return
              if (e.key === 'Enter') this.handleOnClick(e)
            }}
          >
            <div>
              {this.renderTitleRow()}
              {this.renderIdentifierRow(this.getCells([EntityPreviewCardCellType.IDENTIFIER]))}
            </div>
            {this.renderStatusRow(this.getCells([EntityPreviewCardCellType.STATUS]))}
            {this.renderInfoRow(
              this.getCells([
                EntityPreviewCardCellType.INFO,
                EntityPreviewCardCellType.INFO_FROM_ARRAY,
                EntityPreviewCardCellType.NOTES,
              ])
            )}
          </div>
        )}
      </div>
    )
  }

  private renderTitleRow() {
    const { entity } = this.state

    const title = this.props.title ?? entity?.displayName
    if (!title) {
      return null
    }
    return (
      <>
        {this.props.showEntityType && entity?.entityType && (
          <div className="c-entity-preview-card--label mb1">{entity.entityType}</div>
        )}
        <div className="c-entity-preview-card--title">{String(title)}</div>
      </>
    )
  }

  private renderIdentifierRow(filteredCells: IEntityPreviewCardCellProps[]) {
    if (filteredCells.length === 0) {
      return null
    }
    return (
      <div className="c-entity-preview-card--identifiers">
        {filteredCells.map((cell, i) => (
          <div key={i}>{cell.value}</div>
        ))}
      </div>
    )
  }

  private renderInfoRow(cells: IEntityPreviewCardCellProps[]) {
    const filteredCells = _.sortBy(cells, ['type'])

    if (filteredCells.length === 0) {
      return null
    }
    return (
      <div className="c-entity-preview-card--info">
        {filteredCells.map((cell, index) => (
          <div
            key={index}
            className={classNames('c-entity-preview-card--cell', {
              'c-entity-preview-card--cell-notes': cell.type === EntityPreviewCardCellType.NOTES,
            })}
          >
            {cell.label && <div className="c-entity-preview-card--label">{cell.label}</div>}
            <div className={classNames('c-entity-preview-card--cell-value')}>{cell.value}</div>
          </div>
        ))}
      </div>
    )
  }

  private renderStatusRow(filteredCells: IEntityPreviewCardCellProps[]) {
    if (filteredCells.length === 0) {
      return null
    }
    return (
      <div className="c-entity-preview-card--status">
        {filteredCells.map((cell, index) => (
          <StatusBadge key={index} text={cell.value} color={cell.color} />
        ))}
      </div>
    )
  }

  private getCells(types: EntityPreviewCardCellType[]): IEntityPreviewCardCellProps[] {
    const { data } = this.props
    const { entity } = this.state

    /*
     This provides an easy way to expand out arrays of custom key-value pairs into multiple,
     labeled info-type cells.
    */
    const customIdentifierCells: IEntityPreviewCardCellProps[] = _.compact(
      _.flatMap(data || [], (cell) => {
        if (cell.type === EntityPreviewCardCellType.INFO_FROM_ARRAY) {
          return this.buildCustomIdentifierCells(entity, cell)
        }
      })
    )

    return [...(data || []), ...(customIdentifierCells || [])]
      .map((cell) => {
        return this.getCellWithValue(cell, entity)
      })
      .filter((cell) => {
        const exists = !_.isEmpty(cell) && !_.isNil(cell)
        const hasValue = !_.isNil(cell.value) && !_.isEmpty(cell.value)
        return (
          exists &&
          hasValue &&
          _.includes(types, cell.type) &&
          cell.type !== EntityPreviewCardCellType.INFO_FROM_ARRAY // already expanded, exclude from final list
        )
      })
  }

  private getCellWithValue(cell: IEntityPreviewCardCellProps, entity: Entity) {
    if (cell.path) {
      return {
        ...cell,
        value: objectToString(_.get(entity, cell.path, '')),
      }
    } else if (cell.formula) {
      return {
        value: this.evaluateCellFormula(cell.formula, entity),
        ...cell,
      }
    }
    return cell
  }

  /**
   * Evaluates a cell formula. Formulas normally resolve synchronously, but a
   * formula (or a symbol it references) may return a Promise; the resolver
   * returns `undefined` while pending and re-renders once it settles.
   */
  private evaluateCellFormula(formula: string, entity: Entity) {
    return this.formulaResolver.resolve(`${entity?.uniqueId}::${formula}`, () =>
      evaluateExpressionWithScopes(this.props.frames, formula, {
        settings: this.settings,
        ...CustomFormulas,
        ...entity,
      })
    )
  }

  /*
   * Builds an array of individual info cells from a custom identifiers type cell definition.
   */
  private buildCustomIdentifierCells(entity, cell) {
    const results = []
    let customIdentifierValues;
    if (cell.path) {
      customIdentifierValues = _.get(entity, cell.path);
    } else if (cell.formula) {
      customIdentifierValues = this.evaluateCellFormula(cell.formula, entity);
    }
    const groupedIdentifiers = this.groupCustomIdentifiers(
      customIdentifierValues,
      cell.labelPath,
      cell.valuePath
    )
    for (const val of groupedIdentifiers || []) {
      results.push({
        label: val.label,
        value: objectToString(val.value),
        type: EntityPreviewCardCellType.INFO,
      })
    }
    return results
  }

  /*
   Groups an array of identifiers by their label and concatenates their values into a
    comma-separated string.
  */
  private groupCustomIdentifiers(
    identifierArray: any[],
    labelPath: string = 'label',
    valuePath: string = 'value'
  ): any[] {
    if (!Array.isArray(identifierArray)) {
      return []
    }
    const grouped = _.groupBy(identifierArray, (item) => item[labelPath])
    return _.map(grouped, (items, key) => {
      const values = _.map(items, (item) => item[valuePath])
      return {
        label: key,
        value: values.join(', '),
      }
    })
  }

  private handleOnClick = (event) => {
    const { onClick, entityEditSchema, entityDetailSchema, entitySheetMode } = this.props
    const { entity } = this.state
    const openOverlay = this.context?.openOverlay

    event.preventDefault()
    event.stopPropagation()

    if (onClick) {
      onClick({ event, entity, openOverlay })
      return
    }

    const uiSchemaPath =
      entitySheetMode === EntityFormSheetMode.Edit ? entityEditSchema : entityDetailSchema
    const subschema = entity.resolveSubschemaByPath(uiSchemaPath)

    // If enableOnClick and onClick is not defined, default to opening a modal or new tab
    if (subschema && openOverlay) {
      openOverlay(
        <EntityFormSheet entity={entity} uiSchemaPath={uiSchemaPath} mode={entitySheetMode} />
      )
    } else {
      window.open(`/entity/${entity.uniqueId}`, '_blank')
    }
  }

  private fetchValueEntity(uniqueId: string) {
    this.setState({ isLoading: true })
    this.store.findRecordThrottled(uniqueId).then((entity) => {
      this.setState({
        entity,
        isLoading: false,
      })
    })
  }
}
